import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import EditClient from '../../EditClient';
import EntityInfo from '../../Bookkeeping/EntityInfo';
import type { AwLogin, AwNumberingSeries, AwProfile, AwSeriesScheme } from '../../../types';
import type { AwCtx } from '../ctx';
import { btn, card, dmy, errBox, note, pillGreen, pillGrey, pillAmber, sectionHead, table, td, th } from '../ui';

// ── Settings panes (owner 2026-10-13) ───────────────────────────────────────────────────────────
// Company profile and Tax settings read the firm's CLIENT record (clients row) — the merchant's own
// companies row is portal-owned and invisible in Admin — and edit it through the existing EditClient
// modal, so there is one form for a client's identity, not two. Document numbering and portal logins
// are the merchant's: shown read-only, never the logo, with the write paths staying in the portal /
// Portal Accounts where accountability for them already sits.

type P = AwProfile['client'];
const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'contents' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', padding: '5px 0' }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--text-primary)', padding: '5px 0', wordBreak: 'break-word' }}>{value === '' || value === null || value === undefined ? '—' : value}</div>
  </div>
);
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(140px, 200px) 1fr', columnGap: 16 };

function useProfile(ctx: AwCtx) {
  const [p, setP] = useState<P | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    api.awProfile(ctx.clientId).then((d: AwProfile) => { setP(d.client); setErr(''); }).catch((e: any) => setErr(e.message));
  }, [ctx.clientId]);
  useEffect(() => { load(); }, [load, ctx.reload]);
  return { p, err, load };
}

function ProfileCard({ ctx, fields, caption }: { ctx: AwCtx; fields: 'all' | 'tax'; caption?: string }) {
  const { t } = useTranslation();
  const { p, err, load } = useProfile(ctx);
  const [editing, setEditing] = useState(false);
  const yn = (v: number | null) => v === null || v === undefined ? '—' : v === 1 ? t('aw_yes') : t('aw_no');
  const fy = (m: number | null, d: number | null) => m ? `${d || 1}/${m}` : '—';
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', flex: 1 }}>{p?.company_name || ctx.clientName}</div>
        <button onClick={() => setEditing(true)} style={btn}><i className="ti ti-pencil" aria-hidden /> {t('aw_profile_edit')}</button>
      </div>
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      {!p ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : (
        <div style={grid}>
          {fields === 'all' && <>
            <Row label={t('aw_pf_company_name')} value={p.company_name} />
            <Row label={t('aw_pf_client_name')} value={p.client_name} />
            <Row label={t('aw_pf_client_no')} value={p.client_no} />
            <Row label={t('aw_pf_entity_type')} value={p.entity_type} />
            <Row label={t('aw_pf_juristic_form')} value={p.juristic_form} />
            {/* Registered capital is free text on the clients row ("1,000,000.00 THB" as printed on the
                affidavit) — shown as stored. Running it through money() printed 0.00 for every real value. */}
            <Row label={t('aw_pf_registered_capital')} value={p.registered_capital} />
            <Row label={t('aw_pf_address')} value={p.address} />
            <Row label={t('aw_pf_email')} value={p.email} />
            <Row label={t('aw_pf_phone')} value={p.phone} />
          </>}
          <Row label={t('aw_pf_tax_id')} value={p.tax_id} />
          <Row label={t('aw_pf_branch_no')} value={p.branch_no} />
          <Row label={t('aw_pf_has_vat')} value={yn(p.has_vat)} />
          <Row label={t('aw_pf_vat_no')} value={p.vat_no} />
          <Row label={t('aw_pf_vat_reg_date')} value={p.vat_reg_date ? dmy(p.vat_reg_date) : ''} />
          <Row label={t('aw_pf_fy_start')} value={fy(p.fy_start_month, p.fy_start_day)} />
        </div>
      )}
      {caption && <div style={{ ...note, marginTop: 10 }}><i className="ti ti-info-circle" aria-hidden /> {caption}</div>}
      {editing && <EditClient clientId={ctx.clientId} onClose={() => setEditing(false)} onSaved={() => { load(); ctx.bump(); }} />}
    </div>
  );
}

export function CompanyProfilePane({ ctx }: { ctx: AwCtx }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ProfileCard ctx={ctx} fields="all" />
      <EntityInfo clientId={ctx.clientId} periodClosed={ctx.periodClosed} onChanged={ctx.bump} />
    </div>
  );
}

export function TaxSettingsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  return <ProfileCard ctx={ctx} fields="tax" caption={t('aw_tax_settings_caption')} />;
}

const SERIES_KEY: Record<string, string> = { inv: 'aw_series_inv', quo: 'aw_series_quo', rec: 'aw_series_rec', tax: 'aw_series_tax', pro: 'aw_series_pro', cn: 'aw_series_cn', dn: 'aw_series_dn' };
const RESET_KEY: Record<string, string> = { none: 'p_prof_reset_none', year: 'p_prof_reset_year', month: 'p_prof_reset_month', day: 'p_prof_reset_day' };

export function NumberingPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  // The scheme is an OBJECT ({ date, reset, pad } — services/docNumberScheme.ts), never a string:
  // rendered as a child it took the whole React tree down. Spelt out with the portal's own words for
  // the same three settings (p_prof_*), so the merchant and the firm read one description.
  const schemeText = (s: AwSeriesScheme | null | undefined): string => {
    if (!s || typeof s !== 'object') return s ? String(s) : '—';
    const date = s.date === 'none' || !s.date ? t('p_prof_date_none') : s.date;
    return `${date} · ${t(RESET_KEY[s.reset] || RESET_KEY.none)} · ${t('aw_scheme_pad', { n: s.pad })}`;
  };
  const [rows, setRows] = useState<AwNumberingSeries[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awNumbering(ctx.clientId).then((d: any) => { if (alive) setRows(d.series || []); }).catch((e: any) => { if (alive) { setRows([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {rows === null ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : !rows.length ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('aw_no_rows')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_numbering_series')}</th><th style={th}>{t('aw_numbering_prefix')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_numbering_start')}</th><th style={th}>{t('aw_numbering_scheme')}</th><th style={th}>{t('aw_numbering_month')}</th><th style={th}>{t('aw_numbering_next')}</th></tr></thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.key}>
                  <td style={td}>{SERIES_KEY[s.key] ? t(SERIES_KEY[s.key]) : s.key}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{s.prefix}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.start}</td>
                  <td style={td}>{schemeText(s.scheme)}</td>
                  <td style={td}>{s.monthSegment ? t('aw_yes') : t('aw_no')}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{s.preview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={note}>{t('aw_numbering_note')}</div>
    </div>
  );
}

export function LoginsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AwLogin[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awLogins(ctx.clientId).then((d: any) => { if (alive) setRows(d.rows || []); }).catch((e: any) => { if (alive) { setRows([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  const canManage = ctx.me.modules.includes('portal_accounts');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {rows === null ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : !rows.length ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('aw_logins_none')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_logins_email')}</th><th style={th}>{t('aw_logins_name')}</th><th style={th}>{t('aw_logins_active')}</th><th style={th}>{t('aw_logins_must_change')}</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={td}>{r.email}</td><td style={td}>{r.name}</td>
                  <td style={td}><span style={r.active ? pillGreen : pillGrey}>{r.active ? t('aw_yes') : t('aw_no')}</span></td>
                  <td style={td}>{r.must_change_password ? <span style={pillAmber}>{t('aw_yes')}</span> : <span style={pillGrey}>{t('aw_no')}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={sectionHead}>{t('aw_col_actions')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* Account creation / reset / disable stay in Portal Accounts (its own module) — a link, never a re-implementation. */}
        {canManage && (
          <a href="/m/portal_accounts" target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: 'none' }}>
            <i className="ti ti-users" aria-hidden /> {t('aw_logins_manage')} <i className="ti ti-external-link" style={{ fontSize: 12 }} aria-hidden />
          </a>
        )}
        <div style={note}>{t('aw_logins_note')}</div>
      </div>
    </div>
  );
}
